import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../api/client';
import Card from './Card';
import { useTheme } from '../context/ThemeContext';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import { formatDateTime } from '../utils/timezone';

interface ControllerHealth {
  id: string;
  name: string | null;
  isOnline: boolean;
  lastSeen: string | null;
  bayName: string | null;
  locationName: string | null;
}

/**
 * Compact equipment-health widget for the owner dashboard. A down controller is
 * lost revenue, so surface online/offline at-a-glance on open. Self-fetches
 * /api/controllers; renders nothing when the owner has no controllers.
 */
export default function HardwareHealthCard() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [controllers, setControllers] = useState<ControllerHealth[] | null>(null);

  const load = useCallback(() => {
    api<ControllerHealth[]>('/api/controllers')
      .then(setControllers)
      .catch(() => { /* dashboard already surfaces connectivity issues */ });
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  // Nothing to show until loaded, and hide entirely for owners with no hardware.
  if (!controllers || controllers.length === 0) return null;

  const offline = controllers.filter(c => !c.isOnline);
  const allOnline = offline.length === 0;

  return (
    <TouchableOpacity activeOpacity={0.8} onPress={() => router.push('/owner/controllers')}>
      <Card style={styles.card}>
        <View style={styles.headerRow}>
          <View style={styles.titleRow}>
            <Ionicons
              name={allOnline ? 'hardware-chip' : 'warning'}
              size={18}
              color={allOnline ? colors.success : colors.danger}
            />
            <Text style={styles.title}>Equipment</Text>
          </View>
          <Text style={[styles.summary, { color: allOnline ? colors.success : colors.danger }]}>
            {allOnline
              ? `All ${controllers.length} online`
              : `${offline.length} of ${controllers.length} offline`}
          </Text>
        </View>

        {!allOnline && (
          <View style={styles.offlineList}>
            {offline.slice(0, 4).map(c => (
              <View key={c.id} style={styles.offlineRow}>
                <Ionicons name="ellipse" size={8} color={colors.danger} />
                <Text style={styles.offlineName} numberOfLines={1}>
                  {c.bayName || c.name || c.id}
                  {c.locationName ? ` · ${c.locationName}` : ''}
                </Text>
                <Text style={styles.offlineSeen}>
                  {c.lastSeen ? `last seen ${formatDateTime(c.lastSeen)}` : 'never seen'}
                </Text>
              </View>
            ))}
            {offline.length > 4 && (
              <Text style={styles.more}>+{offline.length - 4} more</Text>
            )}
          </View>
        )}
      </Card>
    </TouchableOpacity>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  card: { marginBottom: spacing.md, padding: spacing.md },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  title: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  summary: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  offlineList: { marginTop: spacing.sm, gap: spacing.xs, borderTopWidth: 1, borderTopColor: c.borderLight, paddingTop: spacing.sm },
  offlineRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  offlineName: { flex: 1, fontSize: fontSize.sm, color: c.text },
  offlineSeen: { fontSize: fontSize.xs, color: c.textLight },
  more: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
